import React, { useState, useEffect } from 'react';
import alocacaoService from '../services/alocacao.service';

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  verde:   '#1D9E75', verdeBg: '#f0fff4',
  vermelho:'#e53935', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  borda:   '#e8eaed', cinzaBg: '#fafafa',
  texto:   '#1a1a2e', muted:   '#888',
  branco:  '#fff',
};

const inputStyle = {
  width: '100%', height: '34px', padding: '0 10px',
  fontSize: '13px', fontFamily: 'inherit',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  color: COR.texto, boxSizing: 'border-box',
};

// ════════════════════════════════════════════════════════════════════════════
// AlocarHorasModal — popup pra alocar horas a partir de uma data
// ────────────────────────────────────────────────────────────────────────────
// Props:
//   aberto, analista (objeto do resumo), negocio, usuario,
//   onCancelar, onAplicado (callback após aplicar com sucesso)
// ════════════════════════════════════════════════════════════════════════════
function AlocarHorasModal({ aberto, analista, negocio, usuario, onCancelar, onAplicado }) {
  const [horas, setHoras]                = useState('');
  const [dataInicio, setDataInicio]      = useState('');
  const [horaInicio, setHoraInicio]      = useState('09:00');
  const [permitirSobrepor, setPermitirSobrepor] = useState(false);
  const [permitirDiaNaoUtil, setPermitirDiaNaoUtil] = useState(false);
  const [permitirForaComercial, setPermitirForaComercial] = useState(false);

  const [preview, setPreview]            = useState(null);  // resultado do /calcular
  const [calculando, setCalculando]      = useState(false);
  const [aplicando, setAplicando]        = useState(false);
  const [erro, setErro]                  = useState('');

  // Reset ao abrir/trocar analista
  useEffect(() => {
    if (!aberto) return;
    setHoras(analista?.horas_pendentes ? String(analista.horas_pendentes) : '');

    // Data inicial — prioridade:
    //  1) primeira alocação JÁ EXISTENTE do projeto (entre todos analistas)
    //     → assim que houver alocação criada, novos analistas começam pelo
    //       MESMO ponto inicial do projeto, alinhando o cronograma
    //  2) data_inicio planejada DESSE analista (a primeira pessoa a alocar
    //     usa o que o gestor definiu na sessão Planejamento)
    //  3) hoje (fallback se nada existir)
    let inicialIso;
    let horaInicial = '09:00';
    if (analista?.primeira_alocacao_projeto) {
      const d = new Date(analista.primeira_alocacao_projeto);
      inicialIso = d.toISOString().slice(0, 10);
      horaInicial = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } else if (analista?.data_inicio) {
      const d = new Date(analista.data_inicio);
      inicialIso = d.toISOString().slice(0, 10);
      horaInicial = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } else {
      inicialIso = new Date().toISOString().slice(0, 10);
    }
    setDataInicio(inicialIso);
    setHoraInicio(horaInicial);

    setPermitirSobrepor(false);
    setPermitirDiaNaoUtil(false);
    setPermitirForaComercial(false);
    setPreview(null);
    setErro('');
  }, [aberto, analista]);

  if (!aberto) return null;

  // Constrói payload pro motor
  const montarPayload = () => ({
    colaborador_id: analista.colaborador_id,
    horas: parseFloat(horas),
    data_inicio: `${dataInicio}T${horaInicio}:00`,
    contexto: {
      tipo: 'PROJETO',
      referencia_tabela: 'negocios',
      referencia_id: negocio?.id,
    },
    opcoes: {
      permitirSobreposicao: permitirSobrepor,
      permitirDiaNaoUtil:   permitirDiaNaoUtil,
      permitirForaComercial: permitirForaComercial,
    },
  });

  const validar = () => {
    if (!horas || parseFloat(horas) <= 0) {
      setErro('Informe a quantidade de horas.');
      return false;
    }
    if (!dataInicio) {
      setErro('Informe a data de início.');
      return false;
    }
    return true;
  };

  const handleCalcular = async () => {
    setErro('');
    if (!validar()) return;
    setCalculando(true);
    try {
      const r = await alocacaoService.calcular(montarPayload());
      setPreview(r);
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao calcular: ' + err.message);
    } finally {
      setCalculando(false);
    }
  };

  const handleAplicar = async () => {
    setErro('');
    if (!validar()) return;
    setAplicando(true);
    try {
      const payload = { ...montarPayload(), usuario_id: usuario?.id };
      const r = await alocacaoService.aplicar(payload);
      if (typeof onAplicado === 'function') {
        await onAplicado(r);
      }
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao aplicar: ' + err.message);
    } finally {
      setAplicando(false);
    }
  };

  const formatarBR = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatarBRData = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR');
  };

  const formatarBRHora = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <div onClick={onCancelar} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10001,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%', maxWidth: '720px', maxHeight: '88vh',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column',
        zIndex: 10002, overflow: 'hidden',
        fontFamily: "'Manrope', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '12px',
          backgroundColor: COR.azul, color: COR.branco,
        }}>
          <span style={{ fontSize: '18px' }}>⚡</span>
          <span style={{ fontSize: '15px', fontWeight: '600', flex: 1 }}>
            Alocar Horas — {analista?.nome_amigavel || analista?.nome}
          </span>
          <button onClick={onCancelar} style={{
            border: 'none', background: 'transparent', color: COR.branco,
            fontSize: '20px', cursor: 'pointer',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* Resumo do analista */}
          {analista && (
            <div style={{
              marginBottom: '14px', padding: '10px 14px',
              backgroundColor: COR.azulBg, borderRadius: '5px',
              border: `1px solid ${COR.azul}33`,
              display: 'flex', gap: '20px', fontSize: '12px',
            }}>
              <div>
                <div style={{ color: COR.muted, fontSize: '10px' }}>PREVISTAS</div>
                <div style={{ color: COR.texto, fontWeight: '600' }}>{analista.horas_previstas}h</div>
              </div>
              <div>
                <div style={{ color: COR.muted, fontSize: '10px' }}>JÁ ALOCADAS</div>
                <div style={{ color: COR.verde, fontWeight: '600' }}>{analista.horas_alocadas}h</div>
              </div>
              <div>
                <div style={{ color: COR.muted, fontSize: '10px' }}>PENDENTES</div>
                <div style={{ color: COR.amarelo, fontWeight: '600' }}>{analista.horas_pendentes}h</div>
              </div>
            </div>
          )}

          {/* Form */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
            gap: '12px', marginBottom: '14px',
          }}>
            <Campo label="Horas a alocar">
              <input type="number" value={horas} step="0.5" min="0.5"
                onChange={e => setHoras(e.target.value)}
                style={inputStyle} placeholder="Ex: 8" />
            </Campo>
            <Campo label="A partir de">
              <input type="date" value={dataInicio}
                onChange={e => setDataInicio(e.target.value)}
                style={inputStyle} />
            </Campo>
            <Campo label="Hora">
              <input type="time" value={horaInicio}
                onChange={e => setHoraInicio(e.target.value)}
                style={inputStyle} />
            </Campo>
          </div>

          {/* Toggles */}
          <div style={{
            display: 'flex', gap: '10px', flexWrap: 'wrap',
            marginBottom: '16px',
          }}>
            <Toggle ativo={permitirSobrepor} onChange={setPermitirSobrepor}
              label="Permitir sobrepor com outros eventos/alocações" />
            <Toggle ativo={permitirDiaNaoUtil} onChange={setPermitirDiaNaoUtil}
              label="Permitir dia não útil (feriado/fim de semana)" />
            <Toggle ativo={permitirForaComercial} onChange={setPermitirForaComercial}
              label="Permitir fora do horário comercial (24h)" />
          </div>

          {/* Erro */}
          {erro && (
            <div style={{
              padding: '8px 12px', marginBottom: '12px',
              backgroundColor: COR.vermBg, color: COR.vermelho,
              borderRadius: '5px', fontSize: '12px',
              border: `1px solid ${COR.vermelho}33`,
            }}>{erro}</div>
          )}

          {/* Preview */}
          {preview && (
            <div style={{
              marginTop: '12px', padding: '12px 14px',
              backgroundColor: COR.cinzaBg, borderRadius: '6px',
              border: `1px solid ${COR.borda}`,
            }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: COR.azul, marginBottom: '8px' }}>
                📊 Preview da alocação
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '10px', marginBottom: '10px', fontSize: '12px',
              }}>
                <div>
                  <span style={{ color: COR.muted, fontSize: '10px' }}>SOLICITADAS</span>
                  <div style={{ color: COR.texto, fontWeight: '600' }}>{preview.horasSolicitadas}h</div>
                </div>
                <div>
                  <span style={{ color: COR.muted, fontSize: '10px' }}>ALOCÁVEIS</span>
                  <div style={{ color: COR.verde, fontWeight: '600' }}>{preview.horasAlocadas}h</div>
                </div>
                <div>
                  <span style={{ color: COR.muted, fontSize: '10px' }}>FALTANTES</span>
                  <div style={{ color: preview.horasFaltantes > 0 ? COR.vermelho : COR.muted, fontWeight: '600' }}>
                    {preview.horasFaltantes}h
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '12px', color: COR.muted, marginBottom: '6px' }}>
                <b>{preview.blocos.length}</b> bloco(s) gerado(s)
                {preview.prazoFinal && (
                  <> · Prazo final: <b style={{ color: COR.texto }}>{formatarBRData(preview.prazoFinal)}</b></>
                )}
              </div>

              {preview.horasFaltantes > 0 && (
                <div style={{
                  marginTop: '8px', padding: '6px 10px',
                  backgroundColor: COR.amlBg, borderRadius: '4px',
                  border: `1px solid ${COR.amarelo}33`,
                  fontSize: '11px', color: COR.amarelo,
                }}>
                  ⚠ Não foi possível alocar tudo — faltam {preview.horasFaltantes}h.
                  Tente uma data anterior ou habilite os toggles acima.
                </div>
              )}

              {/* Resumo de violações: aparece se algum bloco tem ao menos uma flag */}
              {(() => {
                const violacoes = {
                  fora: preview.blocos.filter(b => b.fora_horario_comercial).length,
                  diaNao: preview.blocos.filter(b => b.dia_nao_util).length,
                  sobr: preview.blocos.filter(b => b.sobreposto).length,
                };
                const total = violacoes.fora + violacoes.diaNao + violacoes.sobr;
                if (total === 0) return null;
                const partes = [];
                if (violacoes.fora)   partes.push(`${violacoes.fora} fora do horário`);
                if (violacoes.diaNao) partes.push(`${violacoes.diaNao} em dia não útil`);
                if (violacoes.sobr)   partes.push(`${violacoes.sobr} sobreposto(s)`);
                return (
                  <div style={{
                    marginTop: '8px', padding: '8px 12px',
                    backgroundColor: '#fff7ed',
                    border: '1px solid #fdba74',
                    borderRadius: '5px', fontSize: '11px', color: '#9a3412',
                    fontWeight: '500',
                  }}>
                    ⚠ {partes.join(' · ')} — confira na lista abaixo se está OK antes de aplicar.
                  </div>
                );
              })()}

              {/* Lista resumida dos blocos (mostra só os primeiros 8) */}
              <div style={{
                marginTop: '10px', maxHeight: '180px', overflowY: 'auto',
                border: `1px solid ${COR.borda}`, borderRadius: '4px',
                backgroundColor: COR.branco,
              }}>
                {preview.blocos.length === 0 ? (
                  <div style={{ padding: '10px', textAlign: 'center', color: COR.muted, fontSize: '11px' }}>
                    Nenhum bloco gerado. Sem capacidade no horizonte.
                  </div>
                ) : preview.blocos.slice(0, 8).map((b, i) => {
                  const temViolacao = b.fora_horario_comercial || b.dia_nao_util || b.sobreposto;
                  return (
                    <div key={i} style={{
                      padding: '6px 10px', fontSize: '11px',
                      borderBottom: i < preview.blocos.length - 1 ? `1px solid ${COR.borda}` : 'none',
                      borderLeft: temViolacao ? '3px solid #f97316' : '3px solid transparent',
                      display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center',
                    }}>
                      <span style={{ color: COR.texto, fontWeight: '500', minWidth: '90px' }}>
                        {formatarBRData(b.data_inicio)}
                      </span>
                      <span style={{ color: COR.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {formatarBRHora(b.data_inicio)}–{formatarBRHora(b.data_fim)}
                      </span>
                      <span style={{ color: COR.verde, fontWeight: '600' }}>
                        {b.horas}h
                      </span>
                      {b.atravessa_almoco && (
                        <span style={{ color: COR.muted, fontSize: '10px' }}>
                          (atravessa almoço)
                        </span>
                      )}
                      {/* Badges de violação */}
                      {b.fora_horario_comercial && (
                        <BadgeViolacao cor="#9333ea">⚠ fora do horário</BadgeViolacao>
                      )}
                      {b.dia_nao_util && (
                        <BadgeViolacao cor="#dc2626">⚠ dia não útil</BadgeViolacao>
                      )}
                      {b.sobreposto && (
                        <BadgeViolacao cor="#ea580c">⚠ sobreposto</BadgeViolacao>
                      )}
                    </div>
                  );
                })}
                {preview.blocos.length > 8 && (
                  <div style={{
                    padding: '6px 10px', fontSize: '10px', color: COR.muted,
                    textAlign: 'center', fontStyle: 'italic',
                  }}>
                    ... e mais {preview.blocos.length - 8} bloco(s)
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', gap: '8px', justifyContent: 'flex-end',
        }}>
          <button onClick={onCancelar} style={{
            padding: '8px 18px', fontSize: '13px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
          }}>Cancelar</button>

          <button onClick={handleCalcular} disabled={calculando || aplicando}
            style={{
              padding: '8px 18px', fontSize: '13px', fontWeight: '600',
              border: `1px solid ${COR.azul}`, borderRadius: '5px',
              backgroundColor: COR.branco, color: COR.azul,
              cursor: (calculando || aplicando) ? 'not-allowed' : 'pointer',
              opacity: (calculando || aplicando) ? 0.6 : 1,
            }}>
            {calculando ? 'Calculando...' : '🔍 Calcular preview'}
          </button>

          <button onClick={handleAplicar}
            disabled={!preview || preview.blocos.length === 0 || aplicando || calculando}
            style={{
              padding: '8px 22px', fontSize: '13px', fontWeight: '600',
              border: 'none', borderRadius: '5px',
              backgroundColor: (preview && preview.blocos.length > 0 && !aplicando && !calculando)
                ? COR.azul : '#cccccc',
              color: COR.branco,
              cursor: (preview && preview.blocos.length > 0 && !aplicando && !calculando)
                ? 'pointer' : 'not-allowed',
            }}>
            {aplicando ? 'Aplicando...' : '💾 Aplicar'}
          </button>
        </div>
      </div>
    </>
  );
}

function Campo({ label, children }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: '11px', fontWeight: '600',
        color: COR.muted, marginBottom: '4px',
      }}>{label}</label>
      {children}
    </div>
  );
}

// Tag pequenina pra marcar uma violação num bloco do preview.
// Cor parametrizada: violeta (fora horário), vermelho (dia não útil), laranja (sobreposto).
function BadgeViolacao({ cor, children }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 6px',
      fontSize: '9px',
      fontWeight: '700',
      borderRadius: '3px',
      backgroundColor: cor + '15',  // 15 = ~8% alpha
      color: cor,
      border: `1px solid ${cor}55`,
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
    }}>{children}</span>
  );
}

function Toggle({ ativo, onChange, label }) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '6px 12px',
      border: `1px solid ${ativo ? COR.azul : COR.borda}`,
      borderRadius: '5px',
      backgroundColor: ativo ? COR.azulBg : COR.branco,
      color: ativo ? COR.azul : COR.texto,
      fontSize: '11px', fontWeight: '500',
      cursor: 'pointer', userSelect: 'none',
    }}>
      <input type="checkbox" checked={ativo}
        onChange={e => onChange(e.target.checked)}
        style={{ margin: 0 }} />
      {label}
    </label>
  );
}

export default AlocarHorasModal;
